import { useState } from 'react';

import {
  Box,
  Button,
  Center,
  Flex,
  HStack,
  IconButton,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Spacer,
  Spinner,
  Tab,
  TabList,
  Tabs,
  Text,
} from '@chakra-ui/react';
import { ColumnDef, createColumnHelper, PaginationState } from '@tanstack/react-table';

import {
  Badge,
  CloseCircleOutline,
  EmptyState,
  ScheduledGiftRecipient,
  ScheduledGiftRecipientOutcome,
} from '@aslanpay/portal-common-lib';

import { Table } from 'app/components/UI/Table';
import { getPointsTransactionReasonLabel } from 'app/constants/pointsTransactions';
import useScheduledGifts from 'app/hooks/useScheduledGifts';
import formatNumber from 'app/utils/formatting';
import getPointsValue from 'app/utils/points';
import { formatCurrency } from 'app/utils/strings';
import {
  formatScheduledAt,
  getGiftActions,
  getGiftSourceLabel,
  getGiftStateReasonLabel,
  getGiftStatusDisplay,
  getOutcomeReasonLabel,
  getRecipientOutcomeDisplay,
} from 'app/utils/scheduledGifts';

import { ScheduledGiftDetailsModalProps } from './types';

const RECIPIENTS_PAGE_SIZE = 10;

const recipientFilters: { label: string; outcomes?: ScheduledGiftRecipientOutcome[] }[] = [
  { label: 'All' },
  { label: 'Failed', outcomes: ['FAILED'] },
  { label: 'Skipped', outcomes: ['SKIPPED'] },
  { label: 'Sent', outcomes: ['SENT'] },
  { label: 'Pending', outcomes: ['PENDING'] },
];

const columnHelper = createColumnHelper<ScheduledGiftRecipient>();
const recipientColumns = [
  columnHelper.accessor((recipient) => recipient, {
    id: 'name',
    header: 'Employee',
    enableSorting: false,
    cell: (info) => {
      const { firstName, lastName } = info.getValue();
      // A recipient who is no longer on record comes back without a name.
      return firstName || lastName ? `${firstName ?? ''} ${lastName ?? ''}`.trim() : 'Unknown';
    },
  }),
  columnHelper.accessor('outcome', {
    header: 'Outcome',
    enableSorting: false,
    cell: (info) => {
      const { label, badge } = getRecipientOutcomeDisplay(info.getValue());
      return <Badge size="sm" status={badge} value={label} />;
    },
  }),
  columnHelper.accessor('outcomeReason', {
    header: 'Reason',
    enableSorting: false,
    cell: (info) => getOutcomeReasonLabel(info.getValue()) ?? '-',
  }),
] as ColumnDef<ScheduledGiftRecipient, unknown>[];

function DetailsRow({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <Flex justify="space-between" gap="20px">
      <Text textStyle="smallRegular" color="brand.darkGray">
        {label}
      </Text>
      <Box textStyle="smallMedium" color="brand.black" fontWeight="600" textAlign="right">
        {value}
      </Box>
    </Flex>
  );
}

function ScheduledGiftDetailsModal({
  isOpen,
  onClose,
  gift: listGift,
  onEdit,
  onCancel,
  onRetry,
  onSendAgain,
  isRetrying,
  isResending,
}: ScheduledGiftDetailsModalProps) {
  const [filterIndex, setFilterIndex] = useState(0);
  const [{ pageIndex, pageSize }, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: RECIPIENTS_PAGE_SIZE,
  });

  const { data: freshGift } = useScheduledGifts.useFetchScheduledGift(
    isOpen ? listGift.id : undefined
  );
  const gift = freshGift ?? listGift;

  const {
    data: recipients,
    isLoading: isLoadingRecipients,
    isError: isRecipientsError,
  } = useScheduledGifts.useFetchScheduledGiftRecipients(isOpen ? listGift.id : undefined, {
    outcomes: recipientFilters[filterIndex].outcomes,
    page: pageIndex,
    size: pageSize,
  });

  const { label: statusLabel, badge: statusBadge } = getGiftStatusDisplay(gift.status);
  const { canEdit, canCancel, canRetry, canSendAgain } = getGiftActions(gift);

  const renderRecipients = () => {
    if (isRecipientsError) {
      return <EmptyState full text="Sorry, we failed to load the employees for this gift" />;
    }
    if (isLoadingRecipients) {
      return (
        <Center py="40px">
          <Spinner color="brand.purple" size="lg" />
        </Center>
      );
    }
    if (recipients?.list.length) {
      return (
        <Table<ScheduledGiftRecipient>
          columns={recipientColumns}
          data={recipients.list}
          total={recipients.total}
          pageNumber={pageIndex}
          pageSize={pageSize}
          onPaginationChange={setPagination}
          getRowId={(recipient) => recipient.individualId}
        />
      );
    }
    return <EmptyState full text="No employees to show" />;
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="2xl" isCentered scrollBehavior="inside">
      <ModalOverlay />
      <ModalContent data-testid="scheduled-gift-details-modal">
        <ModalHeader>
          <HStack>
            <Text textStyle="h4" fontSize="18px" fontWeight="600">
              Scheduled gift
            </Text>
            <Spacer />
            <IconButton
              variant="link"
              icon={<CloseCircleOutline boxSize="30px" />}
              aria-label="Close scheduled gift details"
              onClick={onClose}
            />
          </HStack>
        </ModalHeader>
        <ModalBody>
          <Flex direction="column" gap="24px">
            <Flex direction="column" gap="12px">
              <DetailsRow
                label="Status"
                value={
                  <Flex direction="column" align="flex-end" gap="4px">
                    <Badge size="sm" status={statusBadge} value={statusLabel} />
                    {gift.stateReason && (
                      <Text textStyle="extraSmallMedium" color="brand.darkGray">
                        {getGiftStateReasonLabel(gift.stateReason)}
                      </Text>
                    )}
                  </Flex>
                }
              />
              <DetailsRow label="Send on" value={formatScheduledAt(gift.scheduledAt)} />
              <DetailsRow
                label="Points per employee"
                value={`${formatNumber(gift.pointsAmount)} (${formatCurrency(
                  getPointsValue(gift.pointsAmount),
                  'GBP'
                )})`}
              />
              <DetailsRow label="Total points" value={formatNumber(gift.totalPoints)} />
              <DetailsRow
                label="Employees"
                value={`${formatNumber(gift.recipientCount)}: ${formatNumber(
                  gift.sentCount
                )} sent, ${formatNumber(gift.skippedCount)} skipped, ${formatNumber(
                  gift.failedCount
                )} failed, ${formatNumber(gift.pendingCount)} pending`}
              />
              <DetailsRow label="Source" value={getGiftSourceLabel(gift.source)} />
              <DetailsRow
                label="Reason"
                value={getPointsTransactionReasonLabel(gift.transactionReason)}
              />
              {gift.notes && <DetailsRow label="Notification reason text" value={gift.notes} />}
              <DetailsRow
                label="Also posted as a recognition"
                value={gift.postAsRecognition ? 'Yes' : 'No'}
              />
            </Flex>

            <Flex direction="column" gap="12px">
              <Text textStyle="h6Medium" color="brand.darkPurple">
                Employees
              </Text>
              <Tabs
                index={filterIndex}
                onChange={(index) => {
                  setFilterIndex(index);
                  setPagination((pagination) => ({ ...pagination, pageIndex: 0 }));
                }}
                variant="enclosed"
                size="sm"
              >
                <TabList>
                  {recipientFilters.map((filter) => (
                    <Tab key={filter.label} data-testid={`recipient-filter-${filter.label}`}>
                      {filter.label}
                    </Tab>
                  ))}
                </TabList>
              </Tabs>
              {renderRecipients()}
            </Flex>
          </Flex>
        </ModalBody>
        <ModalFooter gap="12px" flexWrap="wrap">
          {canCancel && (
            <Button
              variant="outline"
              color="brand.danger"
              onClick={() => onCancel(gift)}
              data-testid="details-cancel-gift-button"
            >
              Cancel gift
            </Button>
          )}
          <Spacer />
          {canSendAgain && (
            <Button
              variant="outline"
              onClick={() => onSendAgain(gift)}
              isLoading={isResending}
              isDisabled={isResending}
              data-testid="send-again-button"
            >
              Send again to failed employees
            </Button>
          )}
          {canRetry && (
            <Button
              colorScheme="brand"
              onClick={() => onRetry(gift)}
              isLoading={isRetrying}
              isDisabled={isRetrying}
              data-testid="retry-gift-button"
            >
              Retry
            </Button>
          )}
          {canEdit && (
            <Button colorScheme="brand" onClick={() => onEdit(gift)} data-testid="edit-gift-button">
              Edit
            </Button>
          )}
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}

export default ScheduledGiftDetailsModal;
